import {BookingType} from "../../models/payment/booking-type";
import {VATRate} from "../../models/common/vat-rate";
import {BusinessCaseInformation} from "../../_modules/location/_models/business-case-information";
import {BusinessCaseType} from "../../_modules/location/_models/_enums/business-case-type";
import {ObjectDescription} from "../../_modules/location/_models/_enums/object-description";
import {GeldtransitPolicy} from "./geldtransit-policy";
import {KassendifferenzPolicy} from "./kassendifferenz-policy";

/**
 * The one stored shape of a counted-cash movement (#820): `amount` is the signed money (negative out of the
 * drawer, positive into it) and `quantity` is +1 for the operation itself; −1 is a reversal. Mirrors
 * `Shared.Core/Calculation/KassenvorgangShape.cs`.
 *
 * DSFinV-K 2.4 carries the direction of a Kassenvorgang in the amount (a Geldtransit to the bank is
 * `…^-100.00:Bar` in the process data, Anhang I p. 120; the BZSt example exports it as `POS_BRUTTO −4200`) and
 * uses a negative `MENGE` for a storno or a return (Tz. 4.2.3/4.2.5). The web close screens always sent this
 * shape; the register sent the magnitude with the direction on the quantity until #820, and the server
 * rewrites that older shape into this one (`BusinessCaseProcessor`). The two factories here mint it directly,
 * so what the screen shows is what is stored.
 */
export class KassenvorgangShape {
  /**
   * The close screens' Geldtransit from its signed money (Endbestand − Ist): negative for cash taken out,
   * positive for cash put in. Cash, not taxable, quantity +1, qualified by direction. The caller does not post
   * zero.
   */
  static geldtransit(signedAmount: number, comment: string | null = null): BusinessCaseInformation {
    return new BusinessCaseInformation(BookingType.Cash, signedAmount, GeldtransitPolicy.qualifierFor(signedAmount),
      comment, BusinessCaseType.Geldtransit, VATRate.NULL);
  }

  /**
   * The close screens' Kassendifferenz from its signed money (Ist − Soll): positive for a surplus, negative
   * for a shortfall. Cash, quantity +1, keyed by direction (#822). The caller does not post zero.
   */
  static kassendifferenz(signedAmount: number, comment: string | null = null): BusinessCaseInformation {
    return new BusinessCaseInformation(BookingType.Cash, signedAmount, ObjectDescription.None, comment,
      BusinessCaseType.DifferenzSollIst, KassendifferenzPolicy.vatRateFor(signedAmount));
  }
}
